import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

import { getFolderContent, API, downloadFiles, compressAllImages } from "./api";
import ExplorerView from "./components/ExplorerView";
import ActionBar from "./components/ActionBar";
import Breadcrumb from "./components/Breadcrumb";
import ConfirmDialog from "./components/ConfirmDialog";
import ConfirmPasswordDialog from "./components/ConfirmPasswordDialog";
import UploadProgress from "./components/UploadProgress";
import NotifyCenter from "./components/NotifyCenter";
import LoginPopup from "./LoginPopup";
import namingRules from "./namingRules";

import "./App.css";

const allowedExt = [
  "jpg", "jpeg", "png", "gif", "webp",
  "mp4", "mov", "avi", "mkv", "webm",
  "m4v", "wmv", "flv", "3gp",
  "pdf",
];

const normalize = (str) =>
  str
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Backend chay sau IIS/ARR reverse proxy - thinh thoang bi timeout/502 khi
// service phia sau restart. Retry + timeout de tu phuc hoi thay vi phai F5.
const FOLDER_LOAD_TIMEOUT_MS = 15000;
const FOLDER_LOAD_MAX_RETRIES = 2;
const FOLDER_LOAD_RETRY_DELAY_MS = 1000;

const isRetryableFolderError = (err) => {
  if (err?.name === "AbortError") return true; // timeout
  if (typeof err?.status === "number") return err.status >= 500; // loi server
  return true; // loi mang (fetch throw truoc khi co response)
};

export default function App() {
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [userName, setUserName] = useState(localStorage.getItem("userName") || "");
  const [username, setUsername] = useState(localStorage.getItem("username") || "");
  const [showLogin, setShowLogin] = useState(!role);

  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState(null);

  const [path, setPath] = useState("");
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const loadSeqRef = useRef(0);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  const [showUploadFolderModal, setShowUploadFolderModal] = useState(false);

  const [search, setSearch] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [folderFiles, setFolderFiles] = useState(null);

  const [confirmData, setConfirmData] = useState(null);

  const [notifyCenter, setNotifyCenter] = useState("");
  const [notifyType, setNotifyType] = useState("success");

  const showPopup = (msg, type = "success") => {
    setNotifyCenter(msg);
    setNotifyType(type);
    setTimeout(() => setNotifyCenter(""), 2000);
  };

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [eta, setEta] = useState(0);

  const lastLoadedRef = useRef(0);
  const lastTimeRef = useRef(Date.now());

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedDownload, setSelectedDownload] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const [compressing, setCompressing] = useState(false);

  const compressNote = "Ảnh JPG/PNG trên 5MB sẽ được tự động nén khi upload.";

  const getUploadNote = () => {
    if (!path) return `Chỉ upload file hợp lệ.\n${compressNote}`;

    const rootFolder = path.split("/")[0];
    const key = normalize(rootFolder);
    const rules = namingRules[key];

    if (!rules) return `Chỉ upload file hợp lệ.\n${compressNote}`;

    return `- Đặt file theo tên: ${rules.prefix}_${rules.nameLabel}.png
- Ví dụ: ${rules.prefix}_${rules.example}.png
- ${compressNote}`;
  };

  // Load folder
  useEffect(() => {
    if (!showLogin) {
      loadFolder(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, showLogin]);

  const loadFolder = async (p) => {
    const seq = ++loadSeqRef.current;
    setFolderLoading(true);
    setFolderError(null);

    for (let attempt = 0; attempt <= FOLDER_LOAD_MAX_RETRIES; attempt++) {
      if (seq !== loadSeqRef.current) return; // co request moi hon, bo ket qua cu

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FOLDER_LOAD_TIMEOUT_MS);

      try {
        const data = await getFolderContent(p, { signal: controller.signal });
        if (seq !== loadSeqRef.current) return;
        setFolders(data.folders || []);
        setImages(data.media || []);
        setFolderLoading(false);
        return;
      } catch (err) {
        if (seq !== loadSeqRef.current) return;

        const canRetry =
          attempt < FOLDER_LOAD_MAX_RETRIES && isRetryableFolderError(err);
        if (canRetry) {
          await sleep(FOLDER_LOAD_RETRY_DELAY_MS * (attempt + 1));
          continue;
        }

        setFolderLoading(false);
        setFolderError(
          err?.name === "AbortError"
            ? "Kết nối đến máy chủ quá lâu, có thể máy chủ đang khởi động lại."
            : "Không kết nối được với máy chủ."
        );
        showPopup("❌ Không mở được thư mục, vui lòng thử lại!", "error");
        return;
      } finally {
        clearTimeout(timer);
      }
    }
  };

  // Chan khong cho dieu huong chong len nhau khi dang tai (vd SMB cham/loi
  // mang) - neu khong, breadcrumb se cu don don them ma noi dung khong doi.
  const goIntoFolder = (folder) => {
    if (folderLoading) return;
    setPath(path ? `${path}/${folder}` : folder);
  };

  const goBack = () => {
    if (folderLoading || !path) return;
    const parts = path.split("/");
    parts.pop();
    setPath(parts.join("/") || "");
  };

  const goToPath = (p) => {
    if (folderLoading) return;
    setPath(p);
  };

  const systemFiles = ["thumbs.db", ".ds_store", "desktop.ini"];

  const validateFiles = (files) => {
    for (let f of files) {
      if (systemFiles.includes(f.name.toLowerCase())) continue;
      const ext = f.name.split(".").pop().toLowerCase();
      if (!allowedExt.includes(ext)) {
        showPopup(`❌ Không hỗ trợ: ${f.name}`, "error");
        return false;
      }
    }
    return true;
  };

  // ================== CREATE FOLDER ==================
  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await axios.post(`${API}/folder/create`, {
        folderName: newFolderName,
        parent: path,
      });
      loadFolder(path);
      showPopup("📁 Tạo thư mục thành công!");
    } catch {
      showPopup("⚠️ Tên thư mục đã tồn tại!", "warning");
    }

    setShowCreateModal(false);
    setNewFolderName("");
  };

  // ================== UPLOAD ==================
  const handleUpload = async (url, formData) => {
    setUploading(true);
    setProgress(0);

    lastLoadedRef.current = 0;
    lastTimeRef.current = Date.now();

    try {
      await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { path: path || "" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          setProgress(percent);

          const now = Date.now();
          const dt = (now - lastTimeRef.current) / 1000;
          const dl = e.loaded - lastLoadedRef.current;

          if (dt > 0) {
            const speedMB = dl / 1024 / 1024 / dt;
            setSpeed(speedMB.toFixed(2));
            const remain = e.total - e.loaded;
            setEta((remain / (speedMB * 1024 * 1024)).toFixed(1));
          }

          lastLoadedRef.current = e.loaded;
          lastTimeRef.current = now;
        },
      });

      loadFolder(path);
      showPopup("🎉 Upload thành công!");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Lỗi không xác định";
      showPopup(`❌ Upload thất bại: ${msg}`, "error");
      console.error("Upload error:", err?.response?.status, err?.response?.data);
    }

    setUploading(false);
  };

  const uploadImages = async () => {
    if (!selectedFiles || !validateFiles(selectedFiles)) return;
    setShowUploadImageModal(false);

    const form = new FormData();
    [...selectedFiles].forEach((f) => form.append("files", f));
    await handleUpload(`${API}/upload`, form);
  };

  const uploadFolder = async () => {
    const filteredFolderFiles = folderFiles
      ? [...folderFiles].filter((f) => !systemFiles.includes(f.name.toLowerCase()))
      : null;
    if (!filteredFolderFiles || !validateFiles(filteredFolderFiles)) return;

    const form = new FormData();
    filteredFolderFiles.forEach((f) =>
      form.append("files", f, f.webkitRelativePath)
    );

    setShowUploadFolderModal(false);
    await handleUpload(`${API}/upload-folder`, form);
  };

  // ================== DOWNLOAD ==================
  const toggleDownloadSelect = (name) => {
    setSelectedDownload((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDownload.length === images.length) {
      setSelectedDownload([]);
    } else {
      setSelectedDownload(images.map((i) => i.name));
    }
  };

  const handleDownload = async () => {
    if (selectedDownload.length === 0) return;
    setDownloading(true);
    try {
      await downloadFiles(path, selectedDownload);
      showPopup("✅ Tải về thành công!");
      setShowDownloadModal(false);
      setSelectedDownload([]);
    } catch {
      showPopup("❌ Tải về thất bại!", "error");
    }
    setDownloading(false);
  };

  // ================== COMPRESS ==================
  const handleCompressAll = async () => {
    if (compressing) return;
    setCompressing(true);
    try {
      const stats = await compressAllImages(5);
      const savedMB = (stats.bytesSaved / 1024 / 1024).toFixed(1);
      showPopup(
        `🗜 Đã nén ${stats.filesCompressed}/${stats.filesScanned} ảnh, tiết kiệm ${savedMB} MB`
      );
      loadFolder(path);
    } catch {
      showPopup("❌ Nén ảnh thất bại!", "error");
    }
    setCompressing(false);
  };

  const askDeleteImage = (name) =>
    setConfirmData({
      message: `Xóa file "${name}"?`,
      onConfirm: () => deleteImage(name),
    });

  const deleteImage = async (name) => {
  try {
    await axios.delete(`${API}/delete-file`, {
      params: {
        path: path || "",
        name: name,
      },
    });

    setConfirmData(null);
    loadFolder(path);
    showPopup("🗑 Xóa file thành công!");
  } catch (err) {
    console.error("Delete image failed:", err);
    showPopup("❌ Xóa file thất bại!", "error");
    setConfirmData(null);
  }
};


  const renameImage = async (oldName, newName) => {
    try {
      await axios.post(`${API}/rename-file`, { path, oldName, newName });
      loadFolder(path);
      showPopup("✔ Đổi tên file thành công!");
    } catch {
      showPopup("⚠️ Tên file đã tồn tại!", "warning");
    }
  };

  const askDeleteFolder = (folder) =>
    setDeleteFolderConfirm({
      folder,
      message: `Xóa thư mục "${folder}"? Vui lòng nhập lại mật khẩu để xác nhận.`,
    });

  const deleteFolder = async (folder) => {
    const fullPath = path ? `${path}/${folder}` : folder;

    await axios.delete(`${API}/delete-folder`, {
      params: { path: fullPath },
    });

    setDeleteFolderConfirm(null);
    loadFolder(path);
    showPopup("🗑 Xóa thư mục thành công!");
  };

  const renameFolder = async (folderName, newName) => {
    try {
      await axios.post(`${API}/rename-folder`, {
        parent: path,
        oldName: folderName,
        newName,
      });
      loadFolder(path);
      showPopup("📁 Đổi tên thư mục thành công!");
    } catch {
      showPopup("⚠️ Tên thư mục đã tồn tại!", "warning");
    }
  };

  if (showLogin) {
    return (
      <LoginPopup
        onLogin={(r, name, u) => {
          localStorage.setItem("role", r);
          localStorage.setItem("userName", name);
          localStorage.setItem("username", u);
          setRole(r);
          setUserName(name);
          setUsername(u);
          setShowLogin(false);
        }}
      />
    );
  }

  return (
    <div className="app-container">
      <header className="main-header">
        <img src="/logo.png" className="header-logo" alt="Logo" />
        <div className="header-title">
          Ảnh Nội Bộ Bệnh Viện
          <span>Hùng Vương Gia Lai</span>
        </div>

        {userName && (
          <span className="header-username">{userName}</span>
        )}
        <button
          className="logout-btn"
          onClick={() => {
            localStorage.removeItem("role");
            localStorage.removeItem("userName");
            localStorage.removeItem("username");
            setRole(null);
            setUserName("");
            setUsername("");
            setPath("");
            setShowLogin(true);
          }}
        >
          Đăng xuất
        </button>
      </header>

      <div className="content-wrapper">
        <Breadcrumb path={path} setPath={goToPath} />
        {folderLoading && <div className="folder-loading">Đang tải thư mục…</div>}
        {!folderLoading && folderError && (
          <div className="folder-error">
            <span>⚠️ {folderError}</span>
            <button className="btn small" onClick={() => loadFolder(path)}>
              Thử lại
            </button>
          </div>
        )}

        <ActionBar
          role={role}
          path={path}
          onCreate={() => setShowCreateModal(true)}
          onUploadImage={() => setShowUploadImageModal(true)}
          onUploadFolder={() => setShowUploadFolderModal(true)}
          onSearch={setSearch}
          onDownload={() => { setSelectedDownload([]); setShowDownloadModal(true); }}
          onCompressAll={handleCompressAll}
          compressing={compressing}
        />

        <ExplorerView
          role={role}
          folders={folders}
          images={images}
          path={path}
          search={search}
          onOpenFolder={goIntoFolder}
          onBack={goBack}
          onDeleteFolder={role === "admin" ? askDeleteFolder : null}
          onDeleteImage={role === "admin" ? askDeleteImage : null}
          onRenameFolder={role === "admin" ? renameFolder : null}
          onRenameImage={renameImage}
        />
      </div>

      {uploading && (
        <UploadProgress progress={progress} speed={speed} eta={eta} />
      )}

      {confirmData && (
        <ConfirmDialog
          message={confirmData.message}
          onConfirm={confirmData.onConfirm}
          onCancel={() => setConfirmData(null)}
        />
      )}

      {deleteFolderConfirm && (
        <ConfirmPasswordDialog
          username={username}
          message={deleteFolderConfirm.message}
          onConfirm={() => deleteFolder(deleteFolderConfirm.folder)}
          onCancel={() => setDeleteFolderConfirm(null)}
        />
      )}

      {notifyCenter && (
        <NotifyCenter message={notifyCenter} type={notifyType} />
      )}


      {role === "admin" && showCreateModal && (
        <div className="modal">
          <div className="modal-box">
            <h3>Tạo thư mục mới</h3>

            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Tên thư mục..."
            />

            <p className="note" style={{ whiteSpace: "pre-line" }}>
              {getUploadNote()}
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={createFolder}>
                Tạo
              </button>
              <button
                className="btn cancel"
                onClick={() => setShowCreateModal(false)}
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadImageModal && (
        <div className="modal">
          <div className="modal-box">
            <h3>Upload ảnh / video / PDF</h3>

            <input
              type="file"
              multiple
              onChange={(e) => setSelectedFiles(e.target.files)}
            />

            <p className="note" style={{ whiteSpace: "pre-line" }}>
              {getUploadNote()}
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={uploadImages}>
                Upload
              </button>
              <button
                className="btn cancel"
                onClick={() => setShowUploadImageModal(false)}
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPLOAD FOLDER */}
      {role === "admin" && showUploadFolderModal && (
        <div className="modal">
          <div className="modal-box">
            <h3>Upload thư mục</h3>

            <input
              type="file"
              webkitdirectory="true"
              multiple
              onChange={(e) => setFolderFiles(e.target.files)}
            />

            <p className="note" style={{ whiteSpace: "pre-line" }}>
              {getUploadNote()}
            </p>

            <div className="modal-actions">
              <button className="btn" onClick={uploadFolder}>
                Upload
              </button>
              <button
                className="btn cancel"
                onClick={() => setShowUploadFolderModal(false)}
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DOWNLOAD MODAL */}
      {showDownloadModal && (
        <div className="modal">
          <div className="modal-box" style={{ maxWidth: 520, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
            <h3>Tải file về</h3>

            <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 8px", fontWeight: 600, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={selectedDownload.length === images.length && images.length > 0}
                onChange={toggleSelectAll}
              />
              Chọn tất cả ({images.length} file)
            </label>

            <div style={{ overflowY: "auto", flex: 1, border: "1px solid #ddd", borderRadius: 8, padding: "4px 0" }}>
              {images.map((item) => (
                <label
                  key={item.name}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "7px 14px", cursor: "pointer",
                    background: selectedDownload.includes(item.name) ? "#e8f0fe" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDownload.includes(item.name)}
                    onChange={() => toggleDownloadSelect(item.name)}
                  />
                  <span style={{ flex: 1, fontSize: 13 }}>{item.name}</span>
                  {item.size > 0 && (
                    <span style={{ fontSize: 11, color: "#999" }}>
                      {item.size < 1024 * 1024
                        ? `${(item.size / 1024).toFixed(1)} KB`
                        : `${(item.size / 1024 / 1024).toFixed(1)} MB`}
                    </span>
                  )}
                </label>
              ))}
              {images.length === 0 && (
                <p style={{ textAlign: "center", color: "#999", padding: 16 }}>Không có file nào</p>
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: 14 }}>
              <button
                className="btn"
                onClick={handleDownload}
                disabled={selectedDownload.length === 0 || downloading}
              >
                {downloading ? "Đang tải..." : `Tải về (${selectedDownload.length})`}
              </button>
              <button className="btn cancel" onClick={() => setShowDownloadModal(false)}>
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="footer-info fancy-footer">
        <span className="footer-brand">
          © {new Date().getFullYear()} Hùng Vương Gia Lai
        </span>
        <span className="footer-brand">
          Powered by Phòng CNTT - leanhquan™
        </span>
      </div>
    </div>
  );
}
